import { PropTypes } from 'prop-types';
import React, { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Icon, Icons } from '@ge/components/icon';
import { Menu, MenuTable, placements } from '@ge/components/menu';
import { Severity } from '@ge/components/severity';
import { TableArrow } from '@ge/components/table';
import { DetailsDismissInert } from '@ge/shared/components/entity-details/dismiss-inert';
import { EntityDetailsContext } from '@ge/shared/context/entity-details-context';
import { killEventPropagation } from '@ge/shared/util/general';
import { globalColors } from '@ge/tokens';

const StyledTitle = styled.div`
  height: 26px;
  border-radius: 5px 5px 0 0;
  background: ${({ theme }) => theme.menu.escalationTitleBackground};
  box-shadow: inset 0 -1px 0 0 #1c252d;
  color: ${({ theme }) => theme.menu.headerColor};
  .menu-title-txt {
    letter-spacing: 0.5px;
    line-height: 13px;
    text-transform: uppercase;
    padding: 7px 11px 6px;
  }
`;

const StyledTr = styled.tr`
  border: none !important;
  td {
    font-size: 11px;
    font-weight: ${(props) => (props.isBold ? 700 : 300)};
  }
`;

const StyledContainer = styled.div`
  opacity: ${(props) => (props.isDisconnected ? 0.4 : 1)};
`;

const TableArrowIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const Styledlevel = styled.span`
  padding-top: 7px;
`;

const ArrowIcon = styled(Icon).attrs((props) => ({
  size: 20,
  icon: props.activatedLevel ? Icons.ARROW_RIGHT_ACTIVE : Icons.ARROW_RIGHT,
  color: props.activatedLevel ? globalColors.white : globalColors.slate4,
}))`
  padding-top: 8px;
`;

export const SiteDamageCategory = ({ componentHealth, children, containerRef }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const { t } = useTranslation(['monitor.sites'], { useSuspense: false });

  const { showDamageDetails, resetDetails, setIsAssetPanel } = useContext(EntityDetailsContext);

  const handleShowMenu = (e) => {
    killEventPropagation(e);
    // setAnchorEl(e.currentTarget?.children[0]);
    setAnchorEl(e.currentTarget);
  };

  const handleMenuClose = () => setAnchorEl(null);

  const handleSelectDamage = (e, id) => {
    killEventPropagation(e);
    e.preventDefault();
    resetDetails();
    setIsAssetPanel(true);
    showDamageDetails(id);
  };

  return (
    <div onMouseEnter={handleShowMenu} onMouseLeave={handleMenuClose}>
      <StyledContainer>{children}</StyledContainer>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        container={containerRef}
        placement={placements.TOP}
        onClick={(e) => {
          e.preventDefault();
          killEventPropagation(e);
        }}
      >
        <DetailsDismissInert>
          <StyledTitle>
            <h4 className="menu-title-txt">{t(`table.ComponentHealth`, 'Component Health')}</h4>
          </StyledTitle>
          <MenuTable disableHover={true}>
            <table>
              <tbody>
                {componentHealth?.map((item, index) => (
                  <StyledTr key={index}>
                    <td align="left">
                      <Styledlevel>
                        <Severity level={item?.priority} />
                      </Styledlevel>
                    </td>
                    <td align="left">
                      <span>{item?.type}</span>
                    </td>
                    <td align="left">
                      <ArrowIcon />
                    </td>
                    <td align="left">
                      <span>{item?.severity}</span>
                    </td>
                    <td align="left">
                      <TableArrowIcon>
                        <div onClick={(e) => handleSelectDamage(e, item?.damageid)}>
                          <TableArrow />
                        </div>
                      </TableArrowIcon>
                    </td>
                  </StyledTr>
                ))}
              </tbody>
            </table>
          </MenuTable>
        </DetailsDismissInert>
      </Menu>
    </div>
  );
};

SiteDamageCategory.propTypes = {
  componentHealth: PropTypes.instanceOf(Array).isRequired,
  children: PropTypes.node,
  containerRef: PropTypes.instanceOf(Object),
};
